import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FasServiceTableComponent } from 'fas-services';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PanelModule } from 'primeng/panel';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { QuestionMachineTranslationService } from '../../../../api/api-services/question-machine-translation.service';
import { IQuestionMachineTranslationStatusVm } from '../../../../api/question-machine-translation-status-vm.model';
import { IExamQuestionAdminMetaVm } from '../../../shared/models/exam-question-admin-meta-vm';
import { MinistryTranslationView, lacksUa, textsToTranslate } from '../question-translations';

/**
 * The "Bez tłumaczenia ministerstwa" tab of the question base: the active questions the ministry catalogue ships
 * without a Ukrainian version. The operator ticks questions and has DeepL translate just those (once; a run over
 * a few questions is the way to check the quality first), then corrects a translation in the details if needed.
 * The API runs the job in the background and the tab follows it. Students see an "unofficial translation" notice on
 * every such question until the ministry publishes its own version, which a catalogue import then puts in place.
 *
 * The list itself comes from the question list (the same data as the other tabs); this component keeps the
 * selection, the run and its progress.
 */
@Component({
  selector: 'app-machine-translation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    ProgressBarModule,
    PanelModule,
    SelectButtonModule,
    TooltipModule,
    FasServiceTableComponent,
  ],
  templateUrl: './machine-translation.component.html',
  styleUrl: './machine-translation.component.scss',
})
export class MachineTranslationComponent implements OnInit, OnChanges {
  private readonly _service = inject(QuestionMachineTranslationService);
  private readonly _messageService = inject(MessageService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  /** Every active question without the ministry's Ukrainian text - the counts and "select all" go over these. */
  @Input() allQuestions: IExamQuestionAdminMetaVm[] = [];
  /** The part of them the table shows (the view below, applied by the question list). */
  @Input() questions: IExamQuestionAdminMetaVm[] = [];
  @Input() loading = false;
  @Input() view: MinistryTranslationView = 'missing';
  @Output() viewChange = new EventEmitter<MinistryTranslationView>();
  /** "Szczegóły" of a row: the question list opens the details (with editing) over this tab's list. */
  @Output() openDetails = new EventEmitter<IExamQuestionAdminMetaVm>();
  /** A run ended: the question list reloads the questions to show the translations. */
  @Output() translationFinished = new EventEmitter<void>();

  /** How often the running job is asked for its progress. */
  readonly pollIntervalMs = 2000;

  readonly status = signal<IQuestionMachineTranslationStatusVm | null>(null);
  readonly loadingStatus = signal(false);
  readonly starting = signal(false);
  readonly stopping = signal(false);
  readonly error = signal<string | null>(null);

  /** Ticked rows; kept across reloads by guid. */
  selected: IExamQuestionAdminMetaVm[] = [];
  missingCount = 0;
  ownCount = 0;
  viewOptions: { label: string; value: MinistryTranslationView }[] = [];
  confirmVisible = false;

  /** After a run, the next list drops from the selection what got translated - what failed stays ticked for a retry. */
  private pruneTranslatedOnNextList = false;
  private pollSubscription?: Subscription;

  readonly columns = [
    {
      field: 'questionNumber',
      header: 'Nr pytania',
      contentClass: 'text-sm',
      sortable: true,
    },
    {
      field: 'question',
      header: 'Treść pytania',
      columnClass: 'question-text-cell',
      customTemplate: 'questionCell',
    },
    {
      field: 'questionUA',
      header: 'Tłumaczenie UA',
      columnClass: 'question-text-cell',
      customTemplate: 'uaCell',
    },
    {
      field: 'uaMachineTranslated',
      header: 'Stan',
      columnClass: 'state-cell',
      customTemplate: 'stateCell',
    },
    {
      field: 'knowledgeType',
      header: 'Rodzaj wiedzy',
      contentClass: 'text-sm',
    },
    {
      field: 'examCategories',
      header: 'Kategoria',
      contentClass: 'text-sm',
    },
  ];

  ngOnInit(): void {
    this.loadStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allQuestions']) {
      this.missingCount = this.allQuestions.filter((q) => lacksUa(q)).length;
      this.ownCount = this.allQuestions.length - this.missingCount;
      this.viewOptions = [
        { label: `Bez tłumaczenia (${this.missingCount})`, value: 'missing' },
        { label: `Tłumaczenie własne (${this.ownCount})`, value: 'own' },
        { label: `Wszystkie (${this.allQuestions.length})`, value: 'all' },
      ];
      this.keepSelection();
    }
  }

  get isRunning(): boolean {
    return !!this.status()?.isRunning;
  }

  get deepLEnabled(): boolean {
    return !!this.status()?.deepLEnabled;
  }

  /** Ticked questions DeepL still has something to fill in for (the others are skipped by the API anyway). */
  get toTranslate(): IExamQuestionAdminMetaVm[] {
    return this.selected.filter((q) => lacksUa(q));
  }

  /** Characters DeepL would count for the ticked questions. */
  get estimatedCharacters(): number {
    return this.toTranslate.reduce((sum, q) => sum + textsToTranslate(q).reduce((s, t) => s + t.length, 0), 0);
  }

  get canTranslate(): boolean {
    return this.deepLEnabled && !this.isRunning && !this.starting() && this.toTranslate.length > 0;
  }

  onViewChange(view: MinistryTranslationView): void {
    this.view = view;
    this.viewChange.emit(view);
  }

  onSelectionChange(selection: IExamQuestionAdminMetaVm[] | null): void {
    this.selected = [...(selection ?? [])];
  }

  /** Every question still without a translation, on every page - not only those shown. */
  selectAllMissing(): void {
    this.selected = this.allQuestions.filter((q) => lacksUa(q));
  }

  clearSelection(): void {
    this.selected = [];
  }

  isSelected(q: IExamQuestionAdminMetaVm): boolean {
    return this.selected.some((s) => s.guid === q.guid);
  }

  details(q: IExamQuestionAdminMetaVm): void {
    this.openDetails.emit(q);
  }

  askToTranslate(): void {
    if (this.canTranslate) {
      this.confirmVisible = true;
    }
  }

  translateSelected(): void {
    this.confirmVisible = false;
    const questions = this.toTranslate;
    if (!this.canTranslate || questions.length === 0) {
      return;
    }
    this.error.set(null);
    this.starting.set(true);
    this._service
      .post({ questionGuids: questions.map((q) => q.guid) })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (status) => {
          this.starting.set(false);
          this.status.set(status);
          this._messageService.add({
            severity: 'info',
            summary: 'Tłumaczenie uruchomione',
            detail: `DeepL tłumaczy ${questions.length} ${this.questionsWord(questions.length)}.`,
          });
          this.startPolling();
          this._cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.starting.set(false);
          this.error.set(this.readError(err, 'Nie udało się uruchomić tłumaczenia.'));
          this._cdr.detectChanges();
          // 409: a run is already going - show it.
          this.loadStatus();
        },
      });
  }

  stop(): void {
    this.stopping.set(true);
    this._service
      .delete()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (status) => {
          this.stopping.set(false);
          this.applyStatus(status);
          this._messageService.add({ severity: 'warn', summary: 'Zatrzymywanie', detail: 'Tłumaczenie zatrzyma się po bieżącej paczce pytań.' });
          this._cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.stopping.set(false);
          this.error.set(this.readError(err, 'Nie udało się zatrzymać tłumaczenia.'));
          this._cdr.detectChanges();
        },
      });
  }

  loadStatus(): void {
    this.loadingStatus.set(true);
    this._service
      .get()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (status) => {
          this.loadingStatus.set(false);
          this.applyStatus(status);
          if (status.isRunning) {
            this.startPolling();
          }
          this._cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.loadingStatus.set(false);
          this.error.set(this.readError(err, 'Nie udało się pobrać stanu tłumaczenia.'));
          this._cdr.detectChanges();
        },
      });
  }

  private startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = timer(this.pollIntervalMs, this.pollIntervalMs)
      .pipe(
        switchMap(() => this._service.get()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (status) => {
          this.applyStatus(status);
          this._cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.stopPolling();
          this.error.set(this.readError(err, 'Utracono łączność z serwerem podczas tłumaczenia. Kliknij „Odśwież”.'));
          this._cdr.detectChanges();
        },
      });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }

  private applyStatus(status: IQuestionMachineTranslationStatusVm): void {
    const previous = this.status();
    this.status.set(status);
    if (!status.isRunning) {
      this.stopPolling();
      // Announce the end once: the status we followed was running a moment ago.
      if (previous?.isRunning && previous.jobId === status.jobId) {
        if (status.state === 'Completed' || status.state === 'Stopped') {
          this._messageService.add({
            severity: status.failedQuestions > 0 ? 'warn' : 'success',
            summary: status.state === 'Stopped' ? 'Tłumaczenie zatrzymane' : 'Tłumaczenie zakończone',
            detail: `Przetłumaczone: ${status.translatedQuestions}, pominięte: ${status.skippedQuestions}, błędy: ${status.failedQuestions}.`,
            life: 10000,
          });
        } else if (status.state === 'Failed') {
          this._messageService.add({
            severity: 'error',
            summary: 'Tłumaczenie nie powiodło się',
            detail: status.errorMessage ?? 'Szczegóły w logu serwera.',
            life: 10000,
          });
        }
        // Whatever the outcome, the questions done so far are saved - show them.
        this.pruneTranslatedOnNextList = true;
        this.translationFinished.emit();
      }
    }
  }

  /** The list was reloaded: tick the same questions in the new rows (and drop what a run just translated). */
  private keepSelection(): void {
    if (this.selected.length === 0) {
      this.pruneTranslatedOnNextList = false;
      return;
    }
    const byGuid = new Map(this.allQuestions.map((q) => [q.guid, q] as const));
    let kept = this.selected.map((q) => byGuid.get(q.guid)).filter((q): q is IExamQuestionAdminMetaVm => !!q);
    if (this.pruneTranslatedOnNextList) {
      kept = kept.filter((q) => lacksUa(q));
      this.pruneTranslatedOnNextList = false;
    }
    this.selected = kept;
  }

  stateLabel(status: IQuestionMachineTranslationStatusVm | null): string {
    switch (status?.state) {
      case 'Running':
        return 'Tłumaczenie trwa…';
      case 'Completed':
        return status.failedQuestions > 0 ? 'Zakończone z błędami' : 'Zakończone';
      case 'Stopped':
        return 'Zatrzymane';
      case 'Failed':
        return 'Nie powiodło się';
      default:
        return 'Brak uruchomienia od startu serwera';
    }
  }

  stateClass(status: IQuestionMachineTranslationStatusVm | null): string {
    switch (status?.state) {
      case 'Running':
        return 'state-badge--running';
      case 'Completed':
        return status.failedQuestions > 0 ? 'state-badge--warning' : 'state-badge--completed';
      case 'Stopped':
        return 'state-badge--warning';
      case 'Failed':
        return 'state-badge--failed';
      default:
        return 'state-badge--idle';
    }
  }

  scopeLabel(s: IQuestionMachineTranslationStatusVm): string {
    if (s.selectedQuestions != null) {
      const skipped = s.selectedQuestions - s.totalQuestions;
      return `zaznaczone: ${s.selectedQuestions}` + (skipped > 0 ? ` (${skipped} już z tłumaczeniem)` : '');
    }
    return s.limit ? `${s.limit} pytań próbnych` : 'wszystkie bez wersji ukraińskiej';
  }

  /** Where the Ukrainian text of a row comes from, for the "Stan" column. */
  rowState(q: IExamQuestionAdminMetaVm): { label: string; detail: string; css: string } {
    if (lacksUa(q)) {
      return isBlankText(q.questionUA)
        ? { label: 'Brak tłumaczenia', detail: 'Kursant widzi to pytanie bez tekstu po ukraińsku.', css: 'row-state--missing' }
        : { label: 'Niepełne tłumaczenie', detail: 'Brakuje tłumaczenia którejś z odpowiedzi.', css: 'row-state--missing' };
    }
    const translatedAt = parseApiDate(q.uaMachineTranslatedAt);
    const editedAt = parseApiDate(q.lastUpdated);
    const edited = !!translatedAt && !!editedAt && editedAt.getTime() - translatedAt.getTime() > 2000;
    const notice = 'Kursant widzi przy pytaniu ikonkę „Tłumaczenie nieoficjalne”.';
    if (edited) {
      return {
        label: 'Tłumaczenie własne, poprawione',
        detail: `${notice} Ostatnia edycja: ${formatDay(editedAt)}${q.lastUpdatedBy ? ', ' + q.lastUpdatedBy : ''}.`,
        css: 'row-state--edited',
      };
    }
    return {
      label: 'Tłumaczenie własne',
      detail: `${notice}${translatedAt ? ' Od ' + formatDay(translatedAt) + '.' : ''}`,
      css: 'row-state--own',
    };
  }

  formatNumber(value: number | null | undefined): string {
    return (value ?? 0).toLocaleString('pl-PL');
  }

  /** UTC from the API (no zone in the value) shown in the operator's local time. */
  formatDateTime(input: string | null | undefined): string {
    const date = parseApiDate(input);
    if (!date) {
      return '';
    }
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  questionsWord(count: number): string {
    if (count === 1) {
      return 'pytanie';
    }
    const lastTwo = count % 100;
    const last = count % 10;
    return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 'pytania' : 'pytań';
  }

  private readError(err: HttpErrorResponse, fallback: string): string {
    if (err.status === 0) {
      return 'Połączenie z serwerem zostało przerwane. Sprawdź sieć i spróbuj ponownie.';
    }
    const body = err.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    return body?.message || body?.Message || body?.errorMessages?.[0] || fallback;
  }
}

function isBlankText(value: string | null | undefined): boolean {
  return !value || !value.trim();
}

/** The API writes the times of GovQuestion without a zone; both come from the same server clock, so they compare as they are. */
function parseApiDate(input: string | null | undefined): Date | null {
  if (!input) {
    return null;
  }
  const date = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(input) ? input : `${input}Z`);
  return isNaN(date.getTime()) ? null : date;
}

function formatDay(date: Date | null): string {
  return date ? date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
}
